import { environment } from '@environment';

/**
 * Lazy facade around @sentry/angular.
 *
 * The SDK is used only for reporting — nothing on the critical rendering path needs it.
 * Importing it statically from the root ErrorHandler, AuthService and the HTTP
 * interceptor put all of it in the initial bundle. Here it is fetched once the page has
 * finished loading (or immediately when something actually needs reporting), and the few
 * calls made before it arrives are queued and replayed.
 *
 * `init()` is deliberately kept to the defaults: the profiling integration was requested
 * without a `profilesSampleRate` and the replay sample rates without a replay
 * integration, so both were inert — but they pulled the profiling and rrweb bundles into
 * the chunk, which measured 542 kB and was fetched while the hero was still rendering.
 */

type SentryModule = typeof import('@sentry/angular');

/** Only the surface this app reports through — see the note on destructuring below. */
type SentryApi = Pick<SentryModule, 'captureException' | 'setUser'>;

let sentryPromise: Promise<SentryApi> | null = null;
let sentry: SentryApi | null = null;

/** Calls issued before the SDK finished loading, replayed in order once it has. */
const queue: Array<(s: SentryApi) => void> = [];

const enabled = () => typeof window !== 'undefined' && environment.production;

function load(): Promise<SentryApi> {
  if (!sentryPromise) {
    // Destructured rather than kept as a namespace object: holding `mod` marks every
    // export as used, so the bundler has to keep replay, profiling, feedback and tracing
    // even though nothing calls them.
    sentryPromise = import('@sentry/angular').then(({ init, captureException, setUser }) => {
      init({
        dsn: 'https://f3a4f3bbce156699aab2e782cd1e7cd0@sentry.bws-dev.com/2',
        release: environment.version,
        environment: 'prod',
      });
      sentry = { captureException, setUser };
      while (queue.length) { queue.shift()!(sentry); }
      return sentry;
    });
  }
  return sentryPromise;
}

/** Queues `fn` until the SDK is available; no-op when reporting is disabled. */
function withSentry(fn: (s: SentryApi) => void): void {
  if (!enabled()) { return; }
  if (sentry) { fn(sentry); return; }
  queue.push(fn);
  void load();
}

/**
 * Warms the SDK up so the first real error does not pay the download latency.
 * Safe to call more than once.
 *
 * Waits for `load` before even asking for an idle slot: a bare requestIdleCallback
 * timeout fired mid-render on a throttled connection and put this download in
 * competition with the hero animation. Reporting is not urgent — nothing is lost by
 * letting the page finish first, and errors thrown before then are queued anyway.
 */
export function preloadSentry(): void {
  if (!enabled() || sentryPromise) { return; }

  const whenIdle = () => {
    const ric = (window as any).requestIdleCallback;
    if (typeof ric === 'function') { ric(() => void load(), { timeout: 10000 }); }
    else { setTimeout(() => void load(), 2000); }
  };

  if (document.readyState === 'complete') { whenIdle(); }
  else { window.addEventListener('load', whenIdle, { once: true }); }
}

export function captureException(error: unknown, context?: { tags?: Record<string, string>; fingerprint?: string[] }): void {
  withSentry(s => s.captureException(error, context));
}

/**
 * An unexpected backend answer: an Error with a stable name and a fingerprint of
 * endpoint and status, so Sentry groups by the failing call instead of by the
 * body text (which produced one `<unknown>` issue per message variant).
 */
export class ServerResponseError extends Error {
  readonly endpoint: string;

  constructor(public readonly status: number, url: string | null, public readonly code: string | undefined, body: unknown) {
    const endpoint = ServerResponseError.endpointOf(url);
    const text = typeof body === 'string' ? body : ((body as { message?: string })?.message || JSON.stringify(body ?? null));
    super(`${status} ${endpoint}${code ? ' ' + code : ''}: ${String(text).slice(0, 300)}`);
    this.name = 'ServerResponseError';
    this.endpoint = endpoint;
  }

  get captureContext() {
    return {
      tags: { 'http.status': String(this.status), 'http.endpoint': this.endpoint, 'api.code': this.code || '' },
      fingerprint: ['server-response', this.endpoint, String(this.status)],
    };
  }

  static endpointOf(url: string | null): string {
    if (!url) { return 'unknown'; }
    let path = url;
    try { path = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://dewabit.com').pathname; }
    catch { path = url.split('?')[0]; }
    return path.replace(/^\/v\d+/, '').replace(/\/\d+(?=\/|$)/g, '/:id');
  }
}

export function setUser(user: { id?: number | string; email?: string } | null): void {
  withSentry(s => s.setUser(user));
}
